// @catalog F.030
// TimeSeriesChart — Evolución de ventas mes a mes (tab Resumen del drawer).
// Wrapper recharts AreaChart con paleta teal del rediseño (consistente con
// /ventas + /margenes que también usan recharts). Tooltip dark estilo MUI.

import { Box, Stack, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtCurrencyK } from "./helpers";

export interface SalesChartPoint {
  label: string;
  value: number;
}

interface TimeSeriesChartProps {
  data: SalesChartPoint[];
  height?: number;
}

interface TooltipPayloadEntry {
  value?: number;
  payload?: SalesChartPoint;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;
  return (
    <Box
      sx={{
        bgcolor: "#0d1220",
        color: "#fff",
        borderRadius: 1,
        px: 1.25,
        py: 1,
        fontSize: 12,
        boxShadow: "0 8px 24px rgba(13, 18, 32, 0.25)",
        pointerEvents: "none",
        minWidth: 140,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: "#9aa0ad",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        alignItems="center"
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              bgcolor: "var(--accent)",
            }}
          />
          <span>Ventas</span>
        </Stack>
        <Box
          component="span"
          sx={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: 500,
          }}
        >
          {fmtCurrencyK(value)}
        </Box>
      </Stack>
    </Box>
  );
}

export function TimeSeriesChart({ data, height = 180 }: TimeSeriesChartProps) {
  if (!data.length) {
    return (
      <Box
        sx={{
          height,
          display: "grid",
          placeItems: "center",
          color: "var(--ink-3)",
          fontSize: 12.5,
        }}
      >
        Sin datos para graficar.
      </Box>
    );
  }
  // Mostrar ~6 ticks en el eje X distribuidos uniformemente, sin saturar
  // cuando hay muchos meses (ej. 41).
  const tickInterval = Math.max(0, Math.floor(data.length / 7));
  return (
    <Box sx={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
        >
          <defs>
            <linearGradient id="tsdrawer-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0e7c66" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#0e7c66" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e8ed"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10.5, fill: "#6b7283" }}
            tickLine={false}
            axisLine={{ stroke: "#e5e8ed" }}
            interval={tickInterval}
          />
          <YAxis
            tick={{ fontSize: 10.5, fill: "#6b7283" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: number) => fmtCurrencyK(value)}
            width={56}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ stroke: "#6b7283", strokeDasharray: "3 3", opacity: 0.4 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#0e7c66"
            strokeWidth={2}
            fill="url(#tsdrawer-area)"
            activeDot={{
              r: 4,
              fill: "#0e7c66",
              stroke: "#fff",
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default TimeSeriesChart;
